import { useEffect, useState, useCallback } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LockIcon from '@mui/icons-material/Lock';
import { api } from '../api';
import { PortalVisualizationDataOut } from '../types/apiRequests';
import { useTranslation } from '../i18n/useTranslation';
import SmartDataRenderer from '../components/SmartDataRenderer';
import { useBranding } from '../contexts/BrandingContext';

/** Unwrap MCP content format: {content: [{text: "json", type: "text"}]} → parsed JSON */
function unwrapMcpContent(data: unknown): unknown {
  if (!data || typeof data !== 'object') return data;
  const obj = data as Record<string, unknown>;
  const content = obj.content;
  if (!Array.isArray(content) || content.length === 0) return data;
  const first = content[0] as Record<string, unknown> | undefined;
  if (!first || typeof first.text !== 'string') return data;
  try {
    return JSON.parse(first.text);
  } catch {
    return first.text;
  }
}

export default function VisualizationPage() {
  const { workflowName, nodeId } = useParams<{ workflowName: string; nodeId: string }>();
  const { getAccessTokenSilently } = useOidcContext();
  const t = useTranslation();
  const branding = useBranding();
  const [vizData, setVizData] = useState<PortalVisualizationDataOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!workflowName || !nodeId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const data = await api.getVisualizationData(token, workflowName, nodeId);
      setVizData(data);
    } catch (e: unknown) {
      const err = e as Error & { status?: number };
      if (err.status === 403) {
        setError('access_denied');
      } else if (err.status === 404) {
        setError('not_found');
      } else {
        setError('generic');
      }
    } finally {
      setLoading(false);
    }
  }, [workflowName, nodeId, getAccessTokenSilently]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f8fa' }}>
        <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff', px: 3, py: 2 }}>
          <Skeleton variant="text" width={200} height={28} />
        </Box>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Skeleton variant="rounded" height={400} sx={{ borderRadius: '12px' }} />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f8fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400, px: 3 }}>
          {error === 'access_denied' ? (
            <>
              <LockIcon sx={{ fontSize: 40, color: '#C24242', mb: 2 }} />
              <Typography sx={{ fontWeight: 700, mb: 1 }}>{t('visualization.accessDenied.title')}</Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>{t('visualization.accessDenied.body')}</Typography>
            </>
          ) : (
            <>
              <AccountTreeIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 2 }} />
              <Typography sx={{ fontWeight: 700, mb: 1 }}>{t('visualization.notFound.title')}</Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>{t('visualization.notFound.body')}</Typography>
            </>
          )}
        </Box>
      </Box>
    );
  }

  if (!vizData) return null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f8fa' }}>
      <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        {branding.logo_url ? (
          <Box component="img" src={branding.logo_url} alt="" sx={{ height: 28, objectFit: 'contain' }} />
        ) : (
          <AccountTreeIcon sx={{ fontSize: 24, color: 'primary.main' }} />
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>{vizData.node_label}</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{vizData.workflow_display_name}</Typography>
        </Box>
        {vizData.last_executed_at && (
          <Chip
            label={new Date(vizData.last_executed_at).toLocaleString()}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500, fontSize: '0.75rem', height: 24, fontVariantNumeric: 'tabular-nums' }}
          />
        )}
      </Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {vizData.status === 'no_data' ? (
          <Box sx={{ textAlign: 'center', py: 12 }}>
            <AccountTreeIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 2 }} />
            <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>{t('visualization.noData')}</Typography>
          </Box>
        ) : (
          <SmartDataRenderer data={unwrapMcpContent(vizData.data)} />
        )}
      </Container>
    </Box>
  );
}
